import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import "../assets/styles/PageStyles.scss";
import Breadcrumbs from "../components/Breadcrumbs";

function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="page-container">
      <Helmet>
        <title>Privacy Policy | Goal Guru</title>
        <meta
          name="description"
          content="Read Goal Guru's Privacy Policy and learn how we collect, use, and protect your data."
        />
        <link rel="canonical" href="https://goalguru.live/privacy-policy/" />
        <meta property="og:title" content="Privacy Policy | Goal Guru" />
        <meta
          property="og:description"
          content="Read Goal Guru's Privacy Policy and learn how we collect, use, and protect your data."
        />
        <meta
          property="og:url"
          content="https://goalguru.live/privacy-policy/"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://goalguru.live/og/privacy-policy.png"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy | Goal Guru" />
        <meta
          name="twitter:description"
          content="Read Goal Guru's Privacy Policy and learn how we collect, use, and protect your data."
        />
        <meta
          name="twitter:image"
          content="https://goalguru.live/og/privacy-policy.png"
        />
      </Helmet>
      <Breadcrumbs items={[{ name: "Privacy Policy" }]} />
      <h1>Privacy Policy</h1>
      <p className="date">Effective Date: July 11, 2026</p>
      <p>
        At Goal Guru, we value your privacy and are committed to protecting your
        personal information. This Privacy Policy explains how we collect, use,
        and protect your data when you use our mobile application ("App").
      </p>

      <h2>1. Information We Collect</h2>
      <ul>
        <li>
          <strong>Personal Information:</strong> When you create an account or
          sign in using Google, we may collect your name, email address, profile
          photo (if available), and notification preferences.
        </li>
        <li>
          <strong>Subscription and Transaction Data:</strong> If you purchase a
          subscription through Google Play or the App Store, we may receive
          limited transaction data from the respective platform (e.g.,
          subscription status, plan type, renewal date). We do not store or
          process payment details directly.
        </li>
        <li>
          <strong>Usage Data:</strong> We collect information about your
          interactions with the App, such as match preferences, alerts,
          settings, and feature usage.
        </li>
        <li>
          <strong>AI Chatbot Data:</strong> When you use the AI chatbot, we may
          collect and store chatbot conversations and related interaction data
          to improve chatbot quality, accuracy, and overall user experience.
          Users should not share sensitive personal information via the chatbot.
        </li>
        <li>
          <strong>Community Chat Data:</strong> When you participate in
          community chat, we collect and store the messages you post, your
          display name and profile photo (if set), timestamps, and any reports
          you submit or that other users submit about your content. Messages
          you post are visible to other users in the same chat room by design.
          Do not share sensitive personal information in community spaces.
        </li>
        <li>
          <strong>Device Information:</strong> We collect data about your device
          type, operating system, app version, and unique identifiers for
          analytics, performance monitoring, and debugging purposes.
        </li>
        <li>
          <strong>Cookies and Tracking Technologies:</strong> We may use cookies
          and similar technologies to enhance performance, analyze usage, and
          personalize your experience.
        </li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <p>We use the collected data to:</p>
      <ul>
        <li>
          Provide and personalize football updates, alerts, and notifications.
        </li>
        <li>Operate, maintain, and improve the AI chatbot.</li>
        <li>
          Operate community features, display your messages to other users, and
          moderate community spaces — including reviewing reports and enforcing
          our community rules through warnings, content removal, suspensions,
          or bans.
        </li>
        <li>Manage your account and subscription status.</li>
        <li>Improve App functionality, performance, and user experience.</li>
        <li>
          Communicate with you regarding updates, support, or changes to our
          services.
        </li>
        <li>Comply with legal obligations and prevent misuse of the App.</li>
      </ul>

      <h2>3. Data Sharing and Disclosure</h2>
      <p>
        We do not sell or rent your personal data. We may share limited
        information only when:
      </p>
      <ul>
        <li>You have provided explicit consent.</li>
        <li>
          Content you post in community spaces — such as chat messages, your
          display name, and profile photo — is visible to other users by
          design.
        </li>
        <li>
          It is required by law or necessary to protect the rights and safety of
          Goal Guru or others.
        </li>
        <li>
          We engage trusted third-party service providers (e.g., Google
          services, RevenueCat, analytics providers) who process data on our
          behalf under strict confidentiality and data protection obligations.
        </li>
      </ul>

      <h2>4. Data Security</h2>
      <p>
        We implement industry-standard technical and organizational measures to
        protect your data from unauthorized access, alteration, or disclosure.
        However, no system is completely secure, and we cannot guarantee
        absolute security.
      </p>

      <h2>5. Third-Party Services</h2>
      <p>
        Goal Guru uses trusted third-party platforms such as Google services
        (for analytics, notifications, AI processing, performance monitoring,
        and data storage), RevenueCat (for subscription management), and Google
        Sign-In (for authentication). These services may collect and process
        limited data in accordance with their own privacy policies.
      </p>

      <h2>6. Data Retention</h2>
      <p>
        We retain personal data, including chatbot interaction data, only for as
        long as necessary to fulfill the purposes described in this policy or as
        required by law. Community chat messages and moderation records
        (including reports and enforcement actions such as bans) are retained
        for as long as needed to operate the community features, enforce our
        rules, and comply with legal obligations; content removed by moderation
        may be retained for a limited period for review and audit purposes. You
        may request deletion of your data or account at any time by contacting{" "}
        <strong>support@goalguru.live</strong>.
      </p>

      <h2>7. Your Rights</h2>
      <p>Depending on your location, you may have the following rights:</p>
      <ul>
        <li>
          <strong>Access and Correction:</strong> Request access to or
          correction of your data.
        </li>
        <li>
          <strong>Deletion:</strong> Request deletion of your data.
        </li>
        <li>
          <strong>Data Portability:</strong> Receive a copy of your data in a
          structured, commonly used format.
        </li>
        <li>
          <strong>Objection and Restriction:</strong> Object to or limit how we
          process your data.
        </li>
        <li>
          <strong>Withdrawal of Consent:</strong> Withdraw consent at any time
          without affecting prior lawful processing.
        </li>
      </ul>

      <h2>8. Children’s Privacy</h2>
      <p>
        Goal Guru is not intended for individuals under the age of 16. We do not
        knowingly collect personal data from minors. If you believe a minor has
        provided personal data, please contact us for prompt deletion.
      </p>

      <h2>9. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy periodically. Updates will take effect
        once published within the App or on our website.
      </p>

      <h2>10. Contact Us</h2>
      <p>
        If you have questions or concerns about this Privacy Policy, please
        contact us at:
      </p>
      <p>
        <strong>Email:</strong> support@goalguru.live
      </p>
    </div>
  );
}

export default PrivacyPolicy;
